import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { get } from '../utils/apiClient';
import PatientContextManager from '../utils/patientContext';
import { logger } from "../utils/logger";
import { usePermissions } from '../hooks/usePermissions';
import { useActiveChart } from './ActiveChartContext';

export interface Patient {
  id: string | number;
  case_id?: string | null;
  case_name?: string | null;
  color?: string;
  [key: string]: unknown;
}

export interface PatientContextValue {
  patients: Patient[];
  selectedPatient: Patient | null;
  loading: boolean;
  error: string | null;
  selectPatient: (patient: Patient | null) => void;
  clearPatient: () => void;
  fetchPatients: () => Promise<void>;
  needsSelection: boolean;
  hasMultiplePatients: boolean;
  hasSinglePatient: boolean;
  hasNoPatients: boolean;
}

const PatientContext = createContext<PatientContextValue | null>(null);

export const usePatient = (): PatientContextValue => {
  const context = useContext(PatientContext);
  if (!context) {
    throw new Error('usePatient must be used within a PatientProvider');
  }
  return context;
};

// PatientProvider owns ONLY the accessible-patients list (fetch + color
// assignment). "Which chart is active" is delegated to ActiveChartContext, the
// single source of truth: selectedPatient is derived from activePatientId, and
// selectPatient/clearPatient forward to switchChart/clearActiveChart. This keeps
// the persistent chip and every chart-scoped surface from drifting apart.
export const PatientProvider = ({ children }: { children: React.ReactNode }) => {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const isStaff = !permissionsLoading && hasPermission('staff.access');
  const { activePatientId, activeCaseId, switchChart, clearActiveChart } = useActiveChart();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPatients = useCallback(async () => {
    if (permissionsLoading) {
      return;
    }
    if (isStaff) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await get('/api/client-portal/patients');
      let data: { patients?: Array<Record<string, unknown>> } = {};
      if (response && response.ok) {
        try {
          data = await response.json();
        } catch {
          data = {};
        }
      }
      const patientsWithColors = PatientContextManager.assignColors(data.patients || []);
      setPatients(patientsWithColors);
      setError(null);
    } catch (err) {
      logger.error('Failed to fetch patients:', err);
      setError((err as Error).message || 'Failed to load patient records');
    } finally {
      setLoading(false);
    }
  }, [permissionsLoading, isStaff]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Derived from the single source of truth. In aggregate view (activePatientId
  // null) there is no selected patient.
  const selectedPatient = useMemo<Patient | null>(() => {
    if (activePatientId == null) return null;
    if (activeCaseId != null) {
      return patients.find((p) =>
        String(p.id) === String(activePatientId)
        && String(p.case_id) === String(activeCaseId)
      ) ?? null;
    }
    const matches = patients.filter((p) => String(p.id) === String(activePatientId));
    return matches.length === 1
      ? matches[0]
      : matches.find((p) => p.case_id == null) ?? null;
  }, [patients, activePatientId, activeCaseId]);

  // Selection delegates to ActiveChartContext: the active chart is global and
  // surface entry decides aggregate-vs-direct.
  const selectPatient = useCallback((patient: Patient | null) => {
    if (patient) {
      switchChart(String(patient.id), patient.case_id ?? null);
    } else {
      clearActiveChart();
    }
  }, [switchChart, clearActiveChart]);

  const clearPatient = useCallback(() => {
    clearActiveChart();
  }, [clearActiveChart]);

  const needsSelection = patients.length > 1 && !selectedPatient;
  const hasNoPatients = !loading && patients.length === 0;

  const value: PatientContextValue = {
    patients,
    selectedPatient,
    loading,
    error,
    selectPatient,
    clearPatient,
    fetchPatients,
    needsSelection,
    hasMultiplePatients: patients.length > 1,
    hasSinglePatient: patients.length === 1,
    hasNoPatients,
  };

  return (
    <PatientContext.Provider value={value}>
      {children}
    </PatientContext.Provider>
  );
};

export default PatientContext;
